/**
 * Integration Helper Utilities
 * Shared utilities for working with integration configurations and components
 */

import { 
  Eye, 
  Settings, 
  RefreshCw, 
  ExternalLink, 
  MapPin, 
  X, 
  LinkIcon, 
  EyeOff, 
  ChevronDown, 
  Archive,
  Wrench
} from 'lucide-react';

// Central helper: extract backend error message consistently
export const extractBackendMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  try {
    return (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  } catch (_) {
    return fallback;
  }
};

// Helper function to extract finding ID for UI state management
export const getFindingId = (finding, index) => {
  return finding?.id || String(finding?.scan_id || index || '');
};

// Email extraction and formatting
export const getEmailOnly = (emailField) => {
  if (!emailField) return '-';
  const match = String(emailField).match(/<([^>]+)>/);
  const value = match ? match[1] : String(emailField);
  return value.trim();
};

// Get vault remediation link from AI remediation result
export const getVaultRemediationLink = (aiRemediationResult) => {
  if (!aiRemediationResult || typeof aiRemediationResult !== 'string') return null;
  
  try {
    const parsed = JSON.parse(aiRemediationResult);
    if (parsed?.source === 'bulk_secret_ingest' && parsed?.status === 'persisted' && parsed?.remediation_link) {
      return parsed.remediation_link;
    }
    return null;
  } catch (_) {
    return null;
  }
};

// Truncate text in the middle (useful for hashes, tokens, etc.)
export const truncateMiddle = (value, front = 8, back = 4) => {
  if (!value) return '-';
  const str = String(value);
  if (str.length <= front + back + 3) return str;
  return `${str.slice(0, front)}...${str.slice(-back)}`;
};

// Format file path for display
export const formatFilePath = (filePath, maxLength = 50) => {
  if (!filePath) return '-';
  const path = String(filePath);
  if (path.length <= maxLength) return path;
  
  // Try to keep the filename and some of the path
  const parts = path.split('/');
  const filename = parts[parts.length - 1];
  
  if (filename.length >= maxLength - 3) {
    return `...${filename.slice(-(maxLength - 3))}`;
  }
  
  const remaining = maxLength - filename.length - 4; // 4 for ".../"
  const pathPart = parts.slice(0, -1).join('/');
  
  if (pathPart.length <= remaining) {
    return path;
  }
  
  return `...${pathPart.slice(-remaining)}/${filename}`;
};

// Status mapping utilities
export const getStatusInfo = (finding) => {
  const isIgnored = finding.finding_status === 'ignored' || finding.is_ignored;
  const hasTicket = !!(finding.jirakey || finding.jira_key || finding.jiraKey || finding.ticket_id);
  const isResolved = finding.status === 'Done' ||
                    finding.jira_status === 'Done' ||
                    finding.status === 'DONE' ||
                    finding.jira_status === 'DONE' ||
                    finding.jira_status === 'RESOLVED';

  // Check for fixing status first (highest priority)
  if (finding.remediation_status === 'processing') {
    return {
      status: 'fixing',
      label: 'Fixing...',
      className: 'bg-gray-100 text-gray-800',
      isProcessing: true
    };
  }
  
  // Check for completed remediation with PR link
  if (finding.remediation_status === 'fixed' && finding.pull_request_url) {
    return {
      status: 'fixed_with_pr',
      label: 'Fixed',
      className: 'bg-green-100 text-green-800',
      hasLink: true,
      link: finding.pull_request_url
    };
  }

  if (isIgnored) {
    return {
      status: 'ignored',
      label: 'Ignored',
      className: 'bg-gray-100 text-gray-800'
    };
  }
  
  if (isResolved) {
    return {
      status: 'resolved',
      label: 'Fixed',
      className: 'bg-blue-100 text-blue-800'
    };
  }
  
  if (hasTicket) {
    return {
      status: 'ticket_created',
      label: 'Ticket Created',
      className: 'bg-green-100 text-green-800'
    };
  }
  
  return {
    status: 'unresolved',
    label: 'Unresolved',
    className: 'bg-yellow-100 text-yellow-800'
  };
};

// Severity mapping
export const getSeverityInfo = (severity) => {
  const severityLower = String(severity || 'unknown').toLowerCase();
  
  const severityConfig = {
    critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
    high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
    medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
    low: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Low' },
    unknown: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Unknown' }
  };
  
  return severityConfig[severityLower] || severityConfig.unknown;
};

// Verification status mapping
export const getVerificationInfo = (verified) => {
  return verified ? {
    status: 'verified',
    label: 'Active',
    className: 'bg-red-100 text-red-800'
  } : {
    status: 'unverified',
    label: 'Unvalidated',
    className: 'bg-amber-100 text-amber-800'
  };
};

// Date formatting
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (error) {
    return '-';
  }
};

// Icon component mapping for actions
export const getIconComponent = (iconName) => {
  const iconMap = {
    'Eye': Eye,
    'Settings': Settings,
    'RefreshCw': RefreshCw,
    'ExternalLink': ExternalLink,
    'MapPin': MapPin,
    'X': X,
    'LinkIcon': LinkIcon,
    'EyeOff': EyeOff,
    'ChevronDown': ChevronDown,
    'Archive': Archive,
    'Tool': Wrench
  };
  
  return iconMap[iconName] || ExternalLink;
};

// Selection state helpers
export const getSelectionHelpers = (filteredFindings, selected) => {
  const visibleIds = (filteredFindings || []).map(f => getFindingId(f));
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected[id]);
  const selectedCount = Object.entries(selected).filter(([id, v]) => v && visibleIds.includes(id)).length;
  const isIndeterminate = selectedCount > 0 && !allSelected;

  return {
    visibleIds,
    allSelected,
    selectedCount,
    isIndeterminate
  };
};

// Filter validation helpers
export const validateFilterAvailability = (sourceFilter, availableSources) => {
  return sourceFilter === 'all' || availableSources.includes(sourceFilter);
};

// Build request filters for API calls
export const buildRequestFilters = (filterType, sourceFilter, additionalFilters = {}) => {
  const filters = { ...additionalFilters };
  
  if (filterType === 'verified' || filterType === 'unverified') {
    const conditions = [];
    
    if (filterType === 'verified') {
      conditions.push({
        field: "verified",
        operator: "eq",
        value: "true"
      });
    } else if (filterType === 'unverified') {
      conditions.push({
        field: "verified",
        operator: "eq",
        value: "false"
      });
    }
    
    if (sourceFilter !== 'all') {
      conditions.push({
        field: "detector_type",
        operator: "eq",
        value: sourceFilter
      });
    }
    
    if (conditions.length > 0) {
      filters.groups = [
        {
          conditions: conditions,
          operator: "AND"
        }
      ];
    }
  } else if (sourceFilter !== 'all') {
    filters.groups = [
      {
        conditions: [
          {
            field: "detector_type",
            operator: "eq",
            value: sourceFilter
          }
        ],
        operator: "AND"
      }
    ];
  }
  
  return filters;
};

// Validation helpers
export const validateCredentials = (config, validationType) => {
  switch (validationType) {
    case 'ticketing':
      const hasJiraUrl = config?.jira_url && config.jira_url.trim() !== '';
      const hasFreshserviceUrl = config?.freshservice_url && config.freshservice_url.trim() !== '';
      return hasJiraUrl || hasFreshserviceUrl;
      
    case 'vault':
      const hasVaultUrl = config?.hashicorp_vault_url && config.hashicorp_vault_url.trim() !== '';
      return hasVaultUrl;
      
    default:
      return true;
  }
};

// Get validation error message
export const getValidationMessage = (validationType) => {
  switch (validationType) {
    case 'ticketing':
      return "Please enter the ticketing credentials in the configuration page";
    case 'vault':
      return "Please enter the vault credentials in the configuration page";
    default:
      return "Please check your configuration";
  }
};

// Generic data processing helpers
export const processApiResponse = (response) => {
  if (!response?.data) {
    return {
      data: [],
      pagination: { has_next: false, last_page: 1, page: 1, per_page: 50, total: 0 }
    };
  }

  return {
    data: response.data.data || [],
    pagination: response.data.pagination || {
      has_next: false,
      last_page: 1,
      page: 1,
      per_page: 50,
      total: 0,
    }
  };
};

// Bulk action result processing
export const processBulkActionResult = (response, actionLabel) => {
  const result = {
    successCount: response.data?.success_count || 0,
    failedCount: response.data?.failed_count || 0,
    errors: {},
    successIds: []
  };

  // Process failed items
  if (response.data?.failed && response.data.failed.length > 0) {
    response.data.failed.forEach(failedItem => {
      const rowId = failedItem.finding_id;
      result.errors[rowId] = true;
    });
  }

  // Process successful items
  if (response.data?.success && response.data.success.length > 0) {
    result.successIds = response.data.success.map(s => s.finding_id);
  }

  return result;
};

// URL parameter replacement
export const replaceUrlParameters = (url, params) => {
  let processedUrl = url;
  Object.entries(params).forEach(([key, value]) => {
    processedUrl = processedUrl.replace(`{${key}}`, value);
  });
  return processedUrl;
};

// Generate shimmer rows for loading states
export const generateShimmerRows = (count = 8) => {
  return Array.from({ length: count }, (_, index) => ({ id: `shimmer-${index}` }));
};

// Debounce utility for search/filter inputs
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Local storage helpers for integration preferences
export const getIntegrationPreferences = (integrationType) => {
  try {
    const prefs = localStorage.getItem(`integration_prefs_${integrationType}`);
    return prefs ? JSON.parse(prefs) : {};
  } catch (error) {
    return {};
  }
};

export const setIntegrationPreferences = (integrationType, preferences) => {
  try {
    localStorage.setItem(`integration_prefs_${integrationType}`, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save integration preferences:', error);
  }
};

// Format numbers for display
export const formatNumber = (num) => {
  if (num == null) return '0';
  const number = Number(num);
  if (isNaN(number)) return '0';
  return number.toLocaleString();
};